import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { PARAMETER_CONFIGS } from '../helpers/typesParameter';

function buildCategoryTree(flatList) {
  
  const map = {};
  const roots = [];
  
  // Primeira passada: criar mapa de todas as categorias
  flatList.forEach(cat => {
    map[cat.codCategoria] = {
      ...cat,
      id: cat.codCategoria,
      nome: cat.descricao,
      parentId: cat.codCategoriaSup || null,
      children: []
    };
  });
  
  // Segunda passada: montar hierarquia
  flatList.forEach(cat => {
    if (cat.codCategoriaSup && map[cat.codCategoriaSup]) {
      map[cat.codCategoriaSup].children.push(map[cat.codCategoria]);
    } else {
      roots.push(map[cat.codCategoria]);
    }
  });
  
  
  return roots;
}

// Função para achatar a hierarquia - transforma todos os descendentes em filhos diretos
function flattenCategoryHierarchy(categories) {
  
  const flattened = categories.map(category => {
    // Se não tem filhos, retorna como está
    if (!category.children || category.children.length === 0) {
      return category;
    }
    
    // Função recursiva para coletar todos os descendentes
    const collectAllDescendants = (nodes) => {
      const descendants = [];
      
      nodes.forEach(node => {
        // Adiciona o próprio nó (sem children para não criar hierarquia)
        descendants.push({
          ...node,
          children: [] // Remove children para achatar
        });
        
        // Se tem filhos, coleta recursivamente
        if (node.children && node.children.length > 0) {
          descendants.push(...collectAllDescendants(node.children));
        }
      });
      
      return descendants;
    };
    
    // Coleta todos os descendentes e coloca como filhos diretos
    const allDescendants = collectAllDescendants(category.children);
    
    
    return {
      ...category,
      children: allDescendants
    };
  });
  
  return flattened;
}

export const useParameterConfig = (parameterType, syncId, existingConfigs = [], onParametersChange, categoryList = [], syncName) => {
  // Estados
  const [configurations, setConfigurations] = useState({
    dfcConfig: [],
    financialGroupConfig: [],
    kpiConfig: [],
    dreConfig: []
  });
  const [selectedCategories, setSelectedCategories] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const [selectedType, setSelectedType] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFlat, setCategoryFlat] = useState([]);
  const [categoryTree, setCategoryTree] = useState([]);

  // Refs para controle
  const initializedRef = useRef(false);
  const lastParamsRef = useRef(null);
  const updatingRef = useRef(false);
  const lastParameterTypeRef = useRef(null);
  const lastDfcNotifiedRef = useRef('');

  // Obter configuração do tipo de parâmetro
  const paramConfig = PARAMETER_CONFIGS[parameterType];
  // Ajustar título para incluir o nome do Sync, seguindo o padrão "Configurando ... para: {syncName}"
  const displayParamConfig = useMemo(() => {
    if (!paramConfig) return paramConfig;
    if (!syncName) return paramConfig;
    const baseTitle = paramConfig.title || '';
    const replaced = baseTitle.startsWith('Configurar') ? baseTitle.replace('Configurar', 'Configurando') : baseTitle;
    return {
      ...paramConfig,
      title: `${replaced} para: ${syncName}`
    };
  }, [paramConfig, syncName]);

  // Deduplicador genérico por (tipo + categoria)
  const dedupeConfigsArray = useCallback((arr) => {
    if (!Array.isArray(arr)) return [];
    const map = new Map();
    arr.forEach(item => {
      const typeValue = Number(
        item.type !== undefined ? item.type :
        item.Type !== undefined ? item.Type :
        item.financialGroup !== undefined ? item.financialGroup :
        item.FinancialGroup !== undefined ? item.FinancialGroup : 0
      );
      const cat = item.codCategoria || item.CodCategoria;
      const composite = `${typeValue}|${cat}`;
      if (!map.has(composite)) {
        map.set(composite, item);
      } else {
        const existing = map.get(composite);
        // Preferir o que possui Id
        if (!existing.Id && !existing.id && (item.Id || item.id)) {
          map.set(composite, item);
        }
      }
    });
    return Array.from(map.values());
  }, []);

  // Função para determinar o tipo correto baseado na categoria
  const getTypeFromCategory = useCallback((category, paramType) => {
    if (paramType === 'dfc') {
      // Preferencialmente, use um campo explícito do backend
      if (category.dfcSection !== undefined) return category.dfcSection;
      if (category.grupoDFC !== undefined) return category.grupoDFC;
      if (category.section !== undefined) return category.section;
      
      // Fallback pelo id (aceita id ou codCategoria) - PRIORIDADE ALTA
      const id = category.id || category.codCategoria;
      if (id) {
        if (id.startsWith('DFC_OP_') || id.includes('OP_')) return 0;
        if (id.startsWith('DFC_INV_') || id.includes('INV_')) return 1;
        if (id.startsWith('DFC_FIN_') || id.includes('FIN_')) return 2;
      }
      
      // Fallback pelo tipo textual - PRIORIDADE BAIXA
      if (category.tipo === 'receita' || category.tipo === 'despesa') return 0;
      if (category.tipo === 'investimento') return 1;
      if (category.tipo === 'financiamento') return 2;
      
      console.warn('⚠️ Categoria DFC sem tipo claro:', category);
      return 0;
    }
    
    if (paramType === 'indicadores') {
      return 0; // CAC = 0
    }
    
    if (paramType === 'gruposFinanceiros') {
      if (category.tipo === 'custo_fixo') return 0;
      if (category.tipo === 'custo_variavel') return 1;
      if (category.tipo === 'despesa_fixa') return 2;
      if (category.tipo === 'despesa_variavel') return 3;
      if (category.tipo === 'investimento') return 4;
    }
    
    return 0; // Default
  }, []);

  // Obter categorias para um tipo específico
  const getCategoriesForType = useCallback((paramType, selectedTypeValue) => {
    if (!categoryTree || categoryTree.length === 0) return [];
    return categoryTree;
  }, [categoryTree]);

  // Montar árvore a partir do categoryList recebido por prop
  useEffect(() => {
    setCategoryFlat(categoryList);
    const tree = buildCategoryTree(categoryList);
    const flattenedTree = flattenCategoryHierarchy(tree);
    setCategoryTree(flattenedTree);
  }, [categoryList]);

  // Resetar inicialização quando mudar de parâmetro
  useEffect(() => {
    if (lastParameterTypeRef.current !== parameterType) {
    
      initializedRef.current = false;
      lastParameterTypeRef.current = parameterType;
    }
  }, [parameterType]);

  // Inicializar configurações a partir de existingConfigs
  useEffect(() => {
    if (!initializedRef.current && existingConfigs && paramConfig) {
      
      let configsToProcess = [];
      
      if (typeof existingConfigs === 'object' && !Array.isArray(existingConfigs)) {
        // Se é um objeto com as configurações por parâmetro, usar como está
        const safe = {
          dfcConfig: dedupeConfigsArray(existingConfigs.dfcConfig || []),
          financialGroupConfig: dedupeConfigsArray(existingConfigs.financialGroupConfig || []),
          kpiConfig: dedupeConfigsArray(existingConfigs.kpiConfig || []),
          dreConfig: existingConfigs.dreConfig || []
        };
        setConfigurations(safe);
        
        // Processar apenas as configurações do parâmetro atual
        const configKey = {
          dfc: 'dfcConfig',
          gruposFinanceiros: 'financialGroupConfig',
          indicadores: 'kpiConfig',
          dre: 'dreConfig'
        }[parameterType];
        
        configsToProcess = dedupeConfigsArray(existingConfigs[configKey] || []);
      } else if (Array.isArray(existingConfigs)) {
        // Se é um array, distribuir para o parâmetro atual
        configsToProcess = dedupeConfigsArray(existingConfigs);
        setConfigurations(prev => ({
          ...prev,
          [paramConfig.configKey]: configsToProcess
        }));
      }
      
      // Marcar categorias selecionadas do parâmetro atual
      const selected = {};
      configsToProcess.forEach(config => {
        const catId = config.codCategoria || config.CodCategoria;
        if (catId) {
          selected[catId] = true;
        }
      });
      
      
      
      setSelectedCategories(selected);
      
      // Definir o tipo selecionado baseado nas configurações existentes
      if (configsToProcess.length > 0) {
        const firstConfig = configsToProcess[0];
        const configType = firstConfig.type !== undefined ? firstConfig.type : 
                          firstConfig.Type !== undefined ? firstConfig.Type :
                          firstConfig.financialGroup !== undefined ? firstConfig.financialGroup :
                          firstConfig.FinancialGroup !== undefined ? firstConfig.FinancialGroup : 0;
        setSelectedType(configType);
      } else {
        // Se não há configurações, definir o primeiro tipo disponível
        if (paramConfig && paramConfig.types) {
          const firstTypeKey = Object.keys(paramConfig.types)[0];
          if (firstTypeKey !== undefined) {
            const firstTypeValue = paramConfig.types[firstTypeKey].value;
            setSelectedType(firstTypeValue);
          }
        }
      }
      
      initializedRef.current = true;
      
      
    }
  }, [existingConfigs, parameterType, paramConfig, dedupeConfigsArray]);

  // Inicializar selectedType com o primeiro tipo disponível, se não houver
  useEffect(() => {
    if ((selectedType === null || selectedType === undefined) && paramConfig && paramConfig.types) {
      const firstTypeKey = Object.keys(paramConfig.types)[0];
      if (firstTypeKey !== undefined) {
        setSelectedType(paramConfig.types[firstTypeKey].value);
      }
    }
  }, [paramConfig, selectedType]);

  // Função para construir objeto de parâmetros
  const buildParametersObject = useCallback(() => {
    if (!paramConfig) return {};

    const configKey = paramConfig.configKey;
    const result = [];
    
    // Obter configurações do tipo atual
    const currentConfigs = configurations[configKey] || [];
    // Se selectedType é null E não há seleções ativas, retornar configurações existentes
    if ((selectedType === null || selectedType === undefined) && Object.keys(selectedCategories).length === 0) {
      return { [configKey]: currentConfigs };
    }
    
    
    
    // PRIMEIRO: Adicionar todas as configurações existentes de outros tipos
    
    currentConfigs.forEach(config => {
      const configType = Number(
        config.type !== undefined ? config.type :
        config.Type !== undefined ? config.Type :
        config.financialGroup !== undefined ? config.financialGroup :
        config.FinancialGroup !== undefined ? config.FinancialGroup : -1
      );
      
      
      // Se é de outro tipo (não o atualmente selecionado), manter como está
      if (configType !== Number(selectedType)) {
        const preservedConfig = {
          Id: config.id || config.Id || null,
          [paramConfig.typeField]: configType,
          CodCategoria: config.codCategoria || config.CodCategoria,
          SyncId: syncId || null
        };
        result.push(preservedConfig);
      }
    });
    
    // SEGUNDO: Função recursiva para coletar categorias selecionadas do tipo atual
    const collectSelected = (nodes) => {
      nodes.forEach(node => {
        const nodeId = node.codCategoria || node.id;
        
        // Se esta categoria está selecionada
        if (selectedCategories[nodeId]) {
          // Encontrar configuração existente se houver
          const existingConfig = currentConfigs.find(c => 
            (c.codCategoria || c.CodCategoria) === nodeId
          );
          
          // Determinar o tipo
          let typeValue = Number(selectedType);
          if (existingConfig) {
            typeValue = Number(
              existingConfig.type !== undefined ? existingConfig.type :
              existingConfig.Type !== undefined ? existingConfig.Type :
              existingConfig.financialGroup !== undefined ? existingConfig.financialGroup :
              existingConfig.FinancialGroup !== undefined ? existingConfig.FinancialGroup :
              selectedType
            );
          }
          
          const newConfig = {
            Id: existingConfig?.id || existingConfig?.Id || null,
            [paramConfig.typeField]: typeValue,
            CodCategoria: nodeId,
            SyncId: syncId || null
          };
          
          result.push(newConfig);
        }
        
        // Processar filhos recursivamente
        if (node.children && node.children.length > 0) {
          collectSelected(node.children);
        }
      });
    };
    
    // TERCEIRO: Coletar todas as categorias selecionadas do tipo atual
    collectSelected(categoryTree);
    
    const finalResult = { [configKey]: dedupeConfigsArray(result) };
    
    // Retornar no formato esperado
    return finalResult;
  }, [categoryTree, paramConfig, selectedCategories, selectedType, syncId, configurations, dedupeConfigsArray]);

  useEffect(() => {
    if (parameterType === 'dfc') {
      if (onParametersChange && configurations?.dfcConfig) {
        const dedupedDfc = dedupeConfigsArray(configurations.dfcConfig || []);
        const dedupedSerialized = JSON.stringify(dedupedDfc);
        if (dedupedSerialized === lastDfcNotifiedRef.current) {
          return;
        }
        lastDfcNotifiedRef.current = dedupedSerialized;
        onParametersChange({
          ...configurations,
          dfcConfig: dedupedDfc
        });
      }
      return;
    }

    if (!updatingRef.current && onParametersChange) {
      const newParams = buildParametersObject();
      const newParamsStr = JSON.stringify(newParams);
      const lastParamsStr = JSON.stringify(lastParamsRef.current);
      
      if (newParamsStr !== lastParamsStr) {
        lastParamsRef.current = newParams;
        updatingRef.current = true;
        
        setConfigurations(prev => {
          // Garantir que prev seja um objeto válido
          const safeConfigs = prev || {
            dfcConfig: [],
            financialGroupConfig: [],
            kpiConfig: [],
            dreConfig: []
          };
          
          // Obter o array atual de configurações
          const configKey = paramConfig.configKey;
          const currentConfigs = safeConfigs[configKey] || [];
          const newConfigs = newParams[configKey] || [];
          // Mesclar configurações existentes com novas
          const mergedConfigs = [...currentConfigs];
          
          // Atualizar ou adicionar novas configurações
          newConfigs.forEach(newConfig => {
            const existingIndex = mergedConfigs.findIndex(c => 
              (c.codCategoria || c.CodCategoria) === newConfig.CodCategoria
            );
            
            if (existingIndex >= 0) {
              // Manter o ID existente se houver
              mergedConfigs[existingIndex] = {
                ...newConfig,
                Id: mergedConfigs[existingIndex].Id || mergedConfigs[existingIndex].id || null
              };
            } else {
              mergedConfigs.push(newConfig);
            }
          });
          
          // Remover apenas configurações do tipo atual que não estão mais selecionadas
          const selectedIds = new Set(newConfigs.map(c => c.CodCategoria));
          
          // Se selectedType é null/undefined OU newConfigs está vazio, preservar tudo
          if (selectedType === null || selectedType === undefined || newConfigs.length === 0) {
            const updated = {
              ...safeConfigs,
              [configKey]: mergedConfigs
            };
            onParametersChange(updated);
            return updated;
          }
          
          let filteredConfigs = mergedConfigs.filter(c => {
            const configType = Number(
              c.type !== undefined ? c.type :
              c.Type !== undefined ? c.Type :
              c.financialGroup !== undefined ? c.financialGroup :
              c.FinancialGroup !== undefined ? c.FinancialGroup : -1
            );
            
            // CORREÇÃO: Manter se:
            // 1. É de outro tipo (preservar TODAS as configurações de outros tipos)
            // 2. OU se é do tipo atual E está nas seleções atuais
            if (configType !== Number(selectedType)) {
              return true; // ✅ SEMPRE manter configurações de outros tipos
            } else {
              return selectedIds.has(c.codCategoria || c.CodCategoria); // ✅ Só manter do tipo atual se estiver selecionado
            }
          });
          // Deduplicar após filtro
          filteredConfigs = dedupeConfigsArray(filteredConfigs);
          
          // Criar novo objeto de configurações mantendo outros parâmetros
          const updated = {
            ...safeConfigs,
            [configKey]: filteredConfigs
          };
          onParametersChange(updated);
          return updated;
        });
        
        setTimeout(() => {
          updatingRef.current = false;
        }, 0);
      }
    }
  }, [selectedCategories, selectedType, buildParametersObject, onParametersChange, paramConfig, parameterType, dedupeConfigsArray, configurations]);

  return {
    // Estado
    configurations,
    selectedCategories,
    expandedCategories,
    selectedType,
    searchTerm,
    paramConfig: displayParamConfig,
    categoryFlat,
    categoryTree,
    
    // Setters
    setConfigurations,
    setSelectedCategories,
    setExpandedCategories,
    setSelectedType,
    setSearchTerm,
    
    // Funções utilitárias
    getTypeFromCategory,
    getCategoriesForType,
    buildParametersObject
  };
}; 
